import React, { useEffect, useMemo, useState } from "react";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useSnackbar } from "notistack";
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DownloadOutlined,
  AddCircleOutlineOutlined,
  LocalOfferOutlined as TagIcon,
  VisibilityOutlined as ExternalVisibilityIcon,
  VisibilityOffOutlined as InternalVisibilityIcon,
  MoreVert as MoreMenuIcon,
} from "@mui/icons-material";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import AttachmentThumbnail from "./AttachmentThumbnail.jsx";
import { getDatumZeit } from "@accredo/fe-shared/services/formatService.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useClaimAttachmentActions } from "../../hooks/useClaimAttachmentActions.jsx";
import PropTypes from "prop-types";
import AttachmentTitleModal from "../modals/AttachmentTitleModal.jsx";
import { useClaimAttachmentPermissions } from "../../hooks/permissions/useClaimAttachmentPermissions.jsx";
import AttachmentUsedInModal from "../modals/AttachmentUsedInModal.jsx";
import EditIcon from "@mui/icons-material/Edit";
import UsedInBadge from "../Form/controls/FilePicker/UsedInBadge.jsx";

const initialPageSize = 5;

export default function AttachmentsTable({
  schaden_id,
  onRowClick = () => {},
  onClickNew = () => {},
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [isUsedInModalOpen, setIsUsedInModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const {
    state: attachmentsState,
    setState: setAttachmentsState,
    toggleClaimAttachmentInternal,
  } = useClaimAttachmentActions();

  const { canEdit, canToggleVisibility } = useClaimAttachmentPermissions();

  const tableKey = "anlagen";
  const storageKey = "v2_table_anlagen";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const rows = attachmentsState?.data || [];
  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Anlagen",
        route: `/data/anlages`,
        exportRoute: `/data/anlages/export`,
        exportFilename: `anlagen_schaden_${schaden_id}`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          filters: {
            schaden_id: schaden_id,
          },
        },
        onStateChange: setAttachmentsState,
      },
    ],
    [schaden_id, defaultRowsPerPage, storageKey]
  );

  const {
    tableStates,
    updateTableState,
    fetchData,
    exportData,
    tableExportStates,
  } = useMultiTableData(tablesConfig, enqueueSnackbar);

  const tableState = tableStates[tableKey] || {};

  const [rowMenuState, setRowMenuState] = useState({
    anchorEl: null,
    menuRowId: null,
  });

  const handleMenuOpen = (event, id) => {
    setRowMenuState({ anchorEl: event.currentTarget, menuRowId: id });
    event?.stopPropagation();
  };
  const handleMenuClose = (event) => {
    event?.stopPropagation();
    setRowMenuState({ anchorEl: null, menuRowId: null });
  };

  const handleToggleInternal = async (attachment, newInternalState) => {
    await toggleClaimAttachmentInternal(attachment, newInternalState, {
      onSuccess: () => {
        enqueueSnackbar(
          newInternalState
            ? "Anlage nicht mehr für VN sichtbar"
            : "Anlage jetzt für VN sichtbar",
          { variant: "success" }
        );
      },
    });
  };

  useEffect(() => {
    fetchData(tableKey);
  }, [schaden_id]);

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const handleEditTitle = (attachment) => {
    setSelectedAttachment(attachment);
    setIsTitleModalOpen(true);
    handleMenuClose();
  };

  const handleEditUsedIn = (attachment) => {
    setSelectedAttachment(attachment);
    setIsUsedInModalOpen(true);
    handleMenuClose();
  };

  const anlagenHeaderDefinition = [
    {
      accessorKey: "icons",
      header: "",
      size: 10,
      enableSorting: false,
      enableColumnFilter: false,
      muiTableBodyCellProps: () => {
        return { align: "center" };
      },
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return (
          <span>
            {objectToRender.intern ? (
              <Tooltip title="intern (für VN nicht sichtbar" key="new">
                <InternalVisibilityIcon sx={{ fontSize: "1.5rem" }} />
              </Tooltip>
            ) : null}
          </span>
        );
      },
    },
    {
      accessorKey: "preview",
      header: "",
      size: 56,
      enableSorting: false,
      enableColumnFilter: false,
      muiTableBodyCellProps: () => {
        return { align: "center" };
      },
      Cell: (tableCellObject, passedItem) => {
        const row = tableCellObject ? tableCellObject.row.original : passedItem;
        return <AttachmentThumbnail previewUrl={row.preview_url} />;
      },
    },
    {
      accessorKey: "filename",
      header: "Dateiname",
      size: 250,
      Cell: ({ cell, row }) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {cell.getValue()}
          </Typography>
          {row.original.used_in?.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {row.original.used_in.map((tag, index) => (
                <UsedInBadge key={index} label={tag} />
              ))}
            </Box>
          )}
        </Box>
      ),
    },
    {
      accessorKey: "bezeichnung",
      header: "Bezeichnung",
      size: 200,
    },
    {
      accessorKey: "filetype",
      header: "Typ",
      size: 80,
    },
    {
      accessorFn: (row) => row.filesize,
      header: "Größe",
      size: 100,
      Cell: ({ row }) => formatFileSize(row.original.filesize),
    },
    {
      accessorKey: "created_at",
      header: "Hochgeladen am",
      size: 150,
      Cell: ({ cell }) => getDatumZeit(cell.getValue()),
    },
    {
      accessorKey: "actions",
      header: "",
      size: 60,
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnHiding: false,
      Cell: (tableCellObject, passedItem) => {
        const row = tableCellObject ? tableCellObject.row.original : passedItem;

        const items = [];
        if (canToggleVisibility(row) && row.intern) {
          items.push(
            <MenuItem
              onClick={(e) => {
                handleMenuClose(e);
                handleToggleInternal(row, false);
              }}
            >
              <ExternalVisibilityIcon
                style={{
                  marginRight: "0.5rem",
                }}
              />
              Für VN freigeben
            </MenuItem>
          );
        } else if (canToggleVisibility(row) && !row.intern) {
          items.push(
            <MenuItem
              onClick={(e) => {
                handleMenuClose(e);
                handleToggleInternal(row, true);
              }}
            >
              <InternalVisibilityIcon
                style={{
                  marginRight: "0.5rem",
                }}
              />
              Für VN nicht sichtbar machen
            </MenuItem>
          );
        }

        if (canEdit(row)) {
          items.push(
            <MenuItem
              onClick={(e) => {
                handleMenuClose(e);
                handleEditTitle(row, e);
              }}
            >
              <EditIcon
                style={{
                  marginRight: "0.5rem",
                }}
              />
              Bezeichnung bearbeiten
            </MenuItem>
          );

          items.push(
            <MenuItem
              onClick={(e) => {
                handleMenuClose(e);
                handleEditUsedIn(row, e);
              }}
            >
              <TagIcon
                style={{
                  marginRight: "0.5rem",
                }}
              />
              Kategorien bearbeiten
            </MenuItem>
          );
        }

        return (
          items.length > 0 && (
            <>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, row.id)}
              >
                <MoreMenuIcon />
              </IconButton>
              <Menu
                anchorEl={rowMenuState.anchorEl}
                open={
                  Boolean(rowMenuState.anchorEl) &&
                  rowMenuState.menuRowId === row.id
                }
                onClose={handleMenuClose}
              >
                {...items}
              </Menu>
            </>
          )
        );
      },
    },
  ];

  const handleExportExcel = () => {
    exportData(tableKey);
  };

  const toolbarActions = [
    {
      type: "button",
      label: "Neue Anlage",
      icon: <AddCircleOutlineOutlined />,
      onClick: onClickNew,
      variant: "primary",
    },
    {
      label: "Excel Export",
      type: "icon",
      variant: "tertiary",
      icon: tableExportStates[tableKey].isExporting ? (
        <CircularProgress color="primary" size={32} thickness={5} />
      ) : (
        <DownloadOutlined />
      ),
      onClick: handleExportExcel,
      disabled: tableExportStates[tableKey].isExporting,
    },
  ];

  return (
    <>
      <DataTable
        columns={anlagenHeaderDefinition}
        data={rows}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
        onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
        onChangePage={(nextOrUpdater) => {
          const base = {
            pageIndex: tableState.pageIndex ?? 0,
            pageSize: tableState.pageSize ?? initialPageSize,
          };
          const next =
            typeof nextOrUpdater === "function"
              ? nextOrUpdater(base)
              : nextOrUpdater;
          if (!next) {
            return;
          }
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        storageKey={storageKey}
        enableHeader
        enableSorting
        enableGlobalFilter={true}
        enableColumnFilters={true}
        enableColumnHiding={true}
        topToolbarIcons={true}
        enableTopToolbar={true}
        toolbarActions={toolbarActions}
        onRowClick={onRowClick}
      />

      <AttachmentTitleModal
        open={isTitleModalOpen}
        onClose={() => {
          setIsTitleModalOpen(false);
          setSelectedAttachment(null);
        }}
        attachment={selectedAttachment}
      />

      <AttachmentUsedInModal
        open={isUsedInModalOpen}
        onClose={() => {
          setIsUsedInModalOpen(false);
          setSelectedAttachment(null);
        }}
        attachment={selectedAttachment}
        schadenId={schaden_id}
      />
    </>
  );
}

AttachmentsTable.propTypes = {
  schaden_id: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  onRowClick: PropTypes.func,
  onClickNewCommunication: PropTypes.func,
};
